import { motion } from "framer-motion";
import { Separator } from "@/components/ui/separator";
import { DollarSign, Globe2, Layers, Clock, CheckCircle } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const proofPoints = [
  {
    icon: DollarSign,
    stat: "$3.3B",
    label: "Systems that Handled IATA Agent & Airline Financial Settlements",
  },
  {
    icon: Layers,
    stat: "$10M",
    label: "Mac Rollout to 1,200+ Staff and 200 Shops Across 4 Countries",
  },
  {
    icon: Globe2,
    stat: "28",
    label: "Countries in Y2K Crisis Management Project",
  },
  {
    icon: Clock,
    stat: "40+",
    label: "Years of Senior Experience",
  },
  {
    icon: CheckCircle,
    stat: "30",
    label: "Countries Lived and Worked In",
  },
];

export default function Proof() {
  return (
    <section
      id="proof"
      data-testid="proof-section"
      className="relative py-24 md:py-36 bg-[#020617]"
    >
      <div className="blueprint-grid absolute inset-0 opacity-10" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.p
          {...fadeInUp}
          transition={{ duration: 0.6 }}
          className="font-body text-fluid-caption tracking-[0.3em] uppercase text-gold mb-4"
        >
          Proof From Global Scale
        </motion.p>

        <motion.h2
          {...fadeInUp}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-heading font-bold text-fluid-h2 uppercase tracking-tight text-slate-50 mb-4"
        >
          Delivered <span className="text-gold-gradient">Locally</span>
        </motion.h2>

        <motion.p
          {...fadeInUp}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="font-body text-fluid-body text-slate-400 max-w-2xl mb-4"
        >
          I have installed systems where failure costs real money.
          I learned to build with discipline because I had to.
          Now I bring that same care to local businesses here in Kingsville, Ontario and the Windsor-Essex Region.
        </motion.p>

        <Separator className="bg-gold/20 mb-16 max-w-xs" />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {proofPoints.map((point, i) => {
            const Icon = point.icon;
            return (
              <motion.div
                key={point.label}
                data-testid={`proof-stat-${i}`}
                className="bg-[#0f172a] border border-slate-800 rounded-sm p-6 text-center hover:border-gold/30 transition-all duration-400"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                <Icon size={24} className="text-gold mx-auto mb-3" />
                <p className="font-heading font-black text-fluid-stat text-slate-50 mb-2">
                  {point.stat}
                </p>
                <p className="font-body text-fluid-caption text-slate-400 tracking-wider uppercase leading-tight">
                  {point.label}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Additional context */}
        <motion.div
          className="mt-16 grid md:grid-cols-3 gap-6"
          {...fadeInUp}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {["Clear outcomes.", "Clean build.", "Reliable install."].map((text, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-1.5 h-8 bg-gold rounded-full" />
              <p className="font-heading font-bold text-fluid-h3 text-slate-300 uppercase tracking-wide">
                {text}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
